import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import AutoSizer from 'components/AutoSizer';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as StageFlowNodeActions from 'workflows/StageFlowNodeActions';
import {StageFlowNodeGraph} from 'workflows/visualization/TaggedNodeGraph';
import {
  createVirtualNodeGraph,
  VIRTUAL_NODE_ID_PREFIX,
  VirtualNodeGraph,
} from 'workflows/visualization/VirtualNodeGraph';
import VirtualNodeGraphVisualization from 'workflows/visualization/VirtualNodeGraphVisualization';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_FLOW_NODE_FULL_SHAPE,
  StageConfiguration,
  StageFlowNode,
} from 'workflows/WorkflowConstants';

interface ConfigureStageFlowNodeModalProps {
  onRequestClose: () => void;
  stageFlowNode?: StageFlowNode;
  stageConfigurationIndex: number;
  stageConfigurations: Array<StageConfiguration>;
  stageConfigurationIdToStageFlowNodes: {[stageConfigurationId: string]: Array<StageFlowNode>};
}

interface State {
  parentIds: Array<string>;
}

class ConfigureStageFlowNodeModal extends React.Component<ConfigureStageFlowNodeModalProps, State> {
  static propTypes = {
    onRequestClose: PropTypes.func.isRequired,
    // If specified, edit existing node, otherwise create new node
    stageFlowNode: STAGE_FLOW_NODE_FULL_SHAPE,
    // If stageFlowNode is specified, the stage configuration at this index should contain stageFlowNode
    stageConfigurationIndex: PropTypes.number.isRequired,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    stageConfigurationIdToStageFlowNodes: PropTypes.objectOf(
      PropTypes.arrayOf(STAGE_FLOW_NODE_FULL_SHAPE.isRequired)
    ).isRequired,
  };

  static open(props: Omit<ConfigureStageFlowNodeModalProps, 'onRequestClose'>): void {
    const newProps = {
      ...props,
      onRequestClose: () => {
        ModalContainer.close(ConfigureStageFlowNodeModal);
      },
    };
    ModalContainer.open(ConfigureStageFlowNodeModal, <ConfigureStageFlowNodeModal {...newProps} />);
  }

  override state: State = {
    parentIds: this.props.stageFlowNode ? this.props.stageFlowNode.parentIds : [],
  };

  // Ideally this should be a prop to the modal
  _createVirtualNodeGraph(): VirtualNodeGraph<StageFlowNodeGraph> {
    // eslint-disable-next-line no-restricted-syntax
    const stageFlowNodes = _.flatten(Object.values(this.props.stageConfigurationIdToStageFlowNodes));
    const stageFlowNodeGraph = new StageFlowNodeGraph(stageFlowNodes, this.props.stageConfigurations);
    let virtualNodeGraph = createVirtualNodeGraph(stageFlowNodeGraph);
    if (this.props.stageFlowNode) {
      virtualNodeGraph = virtualNodeGraph.setParentNodeIds(this.props.stageFlowNode.id, this.state.parentIds);
    } else {
      virtualNodeGraph = virtualNodeGraph.addVirtualNodes([
        {
          id: _.uniqueId(VIRTUAL_NODE_ID_PREFIX),
          parentIds: this.state.parentIds,
          stageIndex: this.props.stageConfigurationIndex,
        },
      ]);
    }
    return virtualNodeGraph;
  }

  _handleClickParentNode = (stageFlowNodeId: string): void => {
    this.setState({
      parentIds: stageFlowNodeId ? [stageFlowNodeId] : [],
    });
  };

  _handleCtrlClickParentNode = (stageFlowNodeId: string): void => {
    if (stageFlowNodeId) {
      const newParentIds = this.state.parentIds.includes(stageFlowNodeId)
        ? this.state.parentIds.filter((parentId) => parentId !== stageFlowNodeId)
        : [...this.state.parentIds, stageFlowNodeId];
      this.setState({
        parentIds: newParentIds,
      });
    }
  };

  _handleSubmit = (): void => {
    if (this.props.stageFlowNode) {
      StageFlowNodeActions.editStageFlowNode(this.props.stageFlowNode.id, {
        parentIds: this.state.parentIds,
      });
    } else {
      StageFlowNodeActions.createStageFlowNode({
        stageConfigurationId: this.props.stageConfigurations.find(
          (stageConfiguration: StageConfiguration) =>
            stageConfiguration.index === this.props.stageConfigurationIndex
        )!.id,
        parentIds: this.state.parentIds,
      });
    }
    this.props.onRequestClose();
  };

  _getClickableNodeIds(): Array<string> {
    const virtualNodeGraph = this._createVirtualNodeGraph();
    return virtualNodeGraph.getNodeIds().filter((nodeId: string) => {
      const stageFlowNodeId = this.props.stageFlowNode
        ? this.props.stageFlowNode.id
        : // We know there is exactly one virtual node in this case, so take the first
          virtualNodeGraph.getVirtualNodeIds()[0];
      if (virtualNodeGraph.isDescendant(nodeId, stageFlowNodeId)) {
        return false;
      } else if (virtualNodeGraph.getTag(nodeId) > virtualNodeGraph.getTag(stageFlowNodeId)) {
        return false;
      } else {
        return true;
      }
    });
  }

  _getButtons(): Array<ModalButton> {
    return [
      {
        key: 'save',
        onClick: this._handleSubmit,
        style: ButtonStyle.PRIMARY,
        submit: true,
        text: 'Save',
      },
    ];
  }

  override render(): JSX.Element {
    const virtualNodeGraph = this._createVirtualNodeGraph();
    return (
      <Modal
        large={true}
        title={this.props.stageFlowNode ? 'Update node' : 'Create node'}
        onRequestClose={this.props.onRequestClose}
        buttons={this._getButtons()}
      >
        <Row>
          <div className="configureStageFlowNodeModal-selectInputSource">
            <FieldLabel label="Select input source" />
          </div>
          <div className="configureParentNodeVisualization-container">
            <AutoSizer disableHeight={true}>
              {({width}) => (
                <VirtualNodeGraphVisualization
                  containerWidth={width}
                  clickableNodeIds={this._getClickableNodeIds()}
                  nodeIdsToEdit={this.props.stageFlowNode ? [this.props.stageFlowNode.id] : []}
                  onClickNode={this._handleClickParentNode}
                  onCtrlClickNode={this._handleCtrlClickParentNode}
                  virtualNodeGraph={virtualNodeGraph}
                  stageConfigurations={this.props.stageConfigurations}
                />
              )}
            </AutoSizer>
          </div>
        </Row>
      </Modal>
    );
  }
}

export default ConfigureStageFlowNodeModal;
